// The upgrade page — console.s3p.dev/upgrade/{slug}. Where a project owner
// buys custom domains (the first premium feature). The Domains panel's
// "not entitled" error links here; so can pricing/marketing.
//
// Payment is ROOT-APP business: this page calls the console's own
// `billing-checkout` function with the buyer's platform session and redirects
// to the Stripe-hosted Checkout it returns. The entitlement is granted only by
// the Stripe webhook — coming back with ?checkout=success means "paid", and
// the grant lands within moments of Stripe's event.
//
// While billing is disabled (secrets/stripe `enabled:false` — the "wired but
// not charging yet" flag), the function answers `billing-disabled` and this
// page says so instead of erroring.

import {
	Alert,
	Badge,
	Button,
	Card,
	Group,
	List,
	Loader,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import {
	IconArrowLeft,
	IconCircleCheck,
	IconCreditCard,
	IconInfoCircle,
	IconWorld,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { RequireSession } from "../../components/RequireSession.tsx";
import { startCheckout as requestCheckout } from "../../lib/billing.ts";
import { projectPath } from "../../lib/hosts.ts";
import { useSession } from "../../session/SessionProvider.tsx";

/** Links to this page travel through chat/email linkifiers that happily glue
 *  trailing punctuation onto the URL ("…/upgrade/console," → slug "console,").
 *  Salvage the valid slug prefix instead of failing the checkout on it. */
function cleanSlug(raw: string | undefined): string {
	return raw?.toLowerCase().match(/^[a-z0-9][a-z0-9-]*/)?.[0] ?? "";
}

function Upgrade() {
	const slug = cleanSlug(useParams().slug);
	const { client } = useSession();
	const [params] = useSearchParams();
	const outcome = params.get("checkout"); // success | cancelled | null
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<{ kind: "info" | "error"; text: string } | null>(null);

	async function startCheckout() {
		if (!client) return;
		setBusy(true);
		setNotice(null);
		try {
			const res = await requestCheckout(client, {
				slug,
				feature: "custom-domains",
				returnUrl: `${window.location.origin}/upgrade/${slug}`,
			});
			if (res.ok) {
				window.location.assign(res.url);
				return; // leaving the page — keep the spinner on
			}
			if (res.reason === "already-entitled") {
				setNotice({ kind: "info", text: "Custom domains are already unlocked for this project." });
			} else {
				setNotice({
					kind: "info",
					text: res.message ?? "Payments aren't live yet — contact the platform operator.",
				});
			}
		} catch (e) {
			setNotice({ kind: "error", text: e instanceof Error ? e.message : String(e) });
		}
		setBusy(false);
	}

	return (
		<Stack maw={560} mx="auto" py="xl" gap="lg">
			<Group gap="xs">
				<Button
					component={Link}
					to={projectPath(slug)}
					variant="subtle"
					size="compact-sm"
					leftSection={<IconArrowLeft size={16} />}
				>
					Back to {slug}
				</Button>
			</Group>

			{outcome === "success" && (
				<Alert color="green" variant="light" icon={<IconCircleCheck />} title="Payment received">
					Thanks! Custom domains unlock the moment Stripe confirms the subscription — usually
					within a few seconds. Head back to the Domains panel and request your domain.
				</Alert>
			)}
			{outcome === "cancelled" && (
				<Alert color="yellow" variant="light" icon={<IconInfoCircle />} title="Checkout cancelled">
					No charge was made. You can restart whenever you're ready.
				</Alert>
			)}

			<Card withBorder radius="md" p="xl">
				<Stack gap="md">
					<Group gap="sm">
						<IconWorld size={26} />
						<Title order={2}>Custom domains</Title>
						<Badge variant="light">Premium</Badge>
					</Group>
					<Text c="dimmed">
						Serve <Text span fw={600}>{slug}</Text> from your own domain — web and email.
					</Text>
					<List size="sm" spacing={6}>
						<List.Item>Your domain on the web app (TLS certificate included)</List.Item>
						<List.Item>Send email from your domain (DKIM/DMARC set up for you)</List.Item>
						<List.Item>
							One registration covers the whole org — subdomains attach free to every project you
							own
						</List.Item>
					</List>
					{notice && (
						<Alert
							color={notice.kind === "error" ? "red" : "blue"}
							variant="light"
							icon={<IconInfoCircle />}
						>
							{notice.text}
						</Alert>
					)}
					<Button
						size="md"
						leftSection={busy ? <Loader size={16} color="white" /> : <IconCreditCard size={18} />}
						onClick={startCheckout}
						disabled={busy || !client}
					>
						Continue to checkout
					</Button>
					<Text size="xs" c="dimmed">
						Payments are handled by Stripe — card details never touch s3p. Cancel any time from
						your receipt's customer-portal link.
					</Text>
				</Stack>
			</Card>
		</Stack>
	);
}

/** Route wrapper: RequireSession needs the concrete path for post-login return. */
export function UpgradeRoute() {
	const slug = cleanSlug(useParams().slug);
	return (
		<RequireSession returnTo={`/upgrade/${slug}`}>
			<Upgrade />
		</RequireSession>
	);
}
